import { useState, useRef, useEffect, useContext } from "react";
import {
  Eye,
  Search,
  ChevronDown,
  Check,
  ImageIcon,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/admin/adminCustomOrderList/AdminCustomOrderList.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

// ─────────────────────────────────────────────────────────────
// DUMMY DATA
// ─────────────────────────────────────────────────────────────
// const DUMMY_REQUESTS = [
//   {
//     id: "CR-2001",
//     customer: { name: "Bilal Khan", avatar: "BK", email: "bilal@email.com" },
//     furnitureType: "Sofa",
//     description:
//       "L-shaped sofa for corner placement, grey or beige velvet fabric.",
//     referenceImages: 2,
//     budgetRange: "PKR 60,000 – 80,000",
//     quotedPrice: null,
//     status: "Quote Pending",
//     createdAt: "2026-04-13",
//   },
//   {
//     id: "CR-2002",
//     customer: { name: "Nadia Hussain", avatar: "NH", email: "nadia@email.com" },
//     furnitureType: "Bed",
//     description:
//       "King size bed with hydraulic storage, sheesham wood, dark walnut finish.",
//     referenceImages: 3,
//     budgetRange: "PKR 80,000 – 1,00,000",
//     quotedPrice: 95000,
//     status: "Quote Sent",
//     createdAt: "2026-04-14",
//   },
//   {
//     id: "CR-2003",
//     customer: { name: "Tariq Mehmood", avatar: "TM", email: "tariq@email.com" },
//     furnitureType: "Wardrobe",
//     description: "6-door sliding wardrobe with mirror panels, white finish.",
//     referenceImages: 1,
//     budgetRange: "PKR 50,000 – 70,000",
//     quotedPrice: 62000,
//     status: "Confirmed",
//     createdAt: "2026-04-12",
//   },
//   {
//     id: "CR-2004",
//     customer: { name: "Hira Baig", avatar: "HB", email: "hira@email.com" },
//     furnitureType: "Dining Table",
//     description:
//       "8-seater round dining table, teak wood, natural finish, intricate carving on legs.",
//     referenceImages: 4,
//     budgetRange: "PKR 40,000 – 60,000",
//     quotedPrice: null,
//     status: "Quote Pending",
//     createdAt: "2026-04-16",
//   },
//   {
//     id: "CR-2005",
//     customer: { name: "Kamran Iqbal", avatar: "KI", email: "kamran@email.com" },
//     furnitureType: "Office Desk",
//     description:
//       "L-shaped executive desk with cable management, matte black finish.",
//     referenceImages: 2,
//     budgetRange: "PKR 30,000 – 45,000",
//     quotedPrice: 38000,
//     status: "Rejected",
//     createdAt: "2026-04-10",
//   },
//   {
//     id: "CR-2006",
//     customer: { name: "Zoya Farhan", avatar: "ZF", email: "zoya@email.com" },
//     furnitureType: "TV Console",
//     description:
//       "Floating TV unit with LED backlight grooves, white and oak combo.",
//     referenceImages: 3,
//     budgetRange: "PKR 25,000 – 40,000",
//     quotedPrice: 32000,
//     status: "Confirmed",
//     createdAt: "2026-04-11",
//   },
// ];

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const FILTER_OPTIONS = [
  "This Month",
  "Last Month",
  "Last 3 Months",
  "All Time",
];
const STATUS_OPTIONS = ["All", "pending", "quoted", "accepted", "rejected"];

const STATUS_CONFIG = {
  pending: { icon: Clock, className: "crl-status--pending" },
  quoted: { icon: Send, className: "crl-status--sent" },
  accepted: { icon: CheckCircle, className: "crl-status--confirmed" },
  rejected: { icon: XCircle, className: "crl-status--rejected" },
};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const filterByPeriod = (requests, period) => {
  if (period === "All Time") return requests;
  const now = new Date();
  const from = new Date();
  if (period === "This Month") from.setDate(1);
  if (period === "Last Month") {
    from.setMonth(from.getMonth() - 1);
    from.setDate(1);
  }
  if (period === "Last 3 Months") from.setMonth(from.getMonth() - 3);
  return requests.filter((r) => {
    const d = new Date(r.createdAt);
    return d >= from && d <= now;
  });
};

// ─────────────────────────────────────────────────────────────
// FILTER DROPDOWN
// ─────────────────────────────────────────────────────────────
const FilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="crl-filter-wrapper" ref={wrapperRef}>
      <button className="crl-filter-btn" onClick={() => setIsOpen((p) => !p)}>
        <span>{value}</span>
        <ChevronDown
          size={16}
          className={`crl-filter-chevron ${isOpen ? "open" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="crl-filter-menu">
          {FILTER_OPTIONS.map((opt) => (
            <div
              key={opt}
              className={`crl-filter-option ${value === opt ? "active" : ""}`}
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
            >
              <span>{opt}</span>
              {value === opt && (
                <Check size={14} className="crl-filter-check" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status];
  const Icon = config?.icon;
  return (
    <span className={`crl-status-badge ${config?.className || ""}`}>
      {Icon && <Icon size={11} />}
      {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// STATUS TABS
// ─────────────────────────────────────────────────────────────
const StatusTabs = ({ value, onChange, requests }) => {
  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] =
      s === "All"
        ? requests.length
        : requests.filter((r) => r.status === s).length;
    return acc;
  }, {});

  return (
    <div className="crl-status-tabs">
      {STATUS_OPTIONS.map((s) => (
        <button
          key={s}
          className={`crl-status-tab ${value === s ? "active" : ""}`}
          onClick={() => onChange(s)}
        >
          {s}
          <span className="crl-status-tab-count">{counts[s]}</span>
        </button>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const CustomRequestList = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [requests, setRequests] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState("All Time");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    viewAllRequests();
  }, []);

  const viewAllRequests = () => {
    axios
      .get(`${baseUrl}/admin/order/showCustomRequests`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setRequests(response.data.requests);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const visibleRequests = (() => {
    let base = filterByPeriod(requests, filterPeriod);
    if (statusFilter !== "All")
      base = base.filter((r) => r.status === statusFilter);
    const q = searchQuery.trim().toLowerCase();
    return q
      ? base.filter(
          (r) =>
            r.id.toLowerCase().includes(q) ||
            r.adcustomer.name.toLowerCase().includes(q) ||
            r.furnitureType.toLowerCase().includes(q),
        )
      : base;
  })();

  // Summary stats
  const pendingCount = requests.filter(
    (r) => r.status === "Quote Pending",
  ).length;
  const sentCount = requests.filter((r) => r.status === "Quote Sent").length;
  const confirmedCount = requests.filter(
    (r) => r.status === "Confirmed",
  ).length;

  return (
    <div className="crl-container">
      {/* ── Header ── */}
      <div className="crl-header">
        <div>
          <h1 className="crl-title">Custom Requests</h1>
          <p className="crl-subtitle">
            Review and manage customer furniture custom requests
          </p>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="crl-stats-row">
        <div className="crl-stat-card">
          <div className="crl-stat-icon crl-stat-icon--orange">
            <MessageSquare size={18} />
          </div>
          <div>
            <p className="crl-stat-label">Total Requests</p>
            <p className="crl-stat-value">{requests.length}</p>
          </div>
        </div>
        <div className="crl-stat-card">
          <div className="crl-stat-icon crl-stat-icon--yellow">
            <Clock size={18} />
          </div>
          <div>
            <p className="crl-stat-label">Quote Pending</p>
            <p className="crl-stat-value">{pendingCount}</p>
          </div>
        </div>
        <div className="crl-stat-card">
          <div className="crl-stat-icon crl-stat-icon--blue">
            <Send size={18} />
          </div>
          <div>
            <p className="crl-stat-label">Quote Sent</p>
            <p className="crl-stat-value">{sentCount}</p>
          </div>
        </div>
        <div className="crl-stat-card">
          <div className="crl-stat-icon crl-stat-icon--green">
            <CheckCircle size={18} />
          </div>
          <div>
            <p className="crl-stat-label">Confirmed</p>
            <p className="crl-stat-value">{confirmedCount}</p>
          </div>
        </div>
      </div>

      {/* ── Status Tabs ── */}
      <StatusTabs
        value={statusFilter}
        onChange={setStatusFilter}
        requests={requests}
      />

      {/* ── Controls ── */}
      <div className="crl-controls">
        <div className="crl-search">
          <Search size={15} className="ad-crl-search-icon" />
          <input
            type="text"
            className="crl-search-input"
            placeholder="Search by ID, customer or furniture type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <FilterDropdown value={filterPeriod} onChange={setFilterPeriod} />
      </div>

      {/* ── Table ── */}
      <div className="crl-table-container">
        {visibleRequests.length === 0 ? (
          <div className="crl-empty">
            <span className="crl-empty-icon">📋</span>
            <h3>No requests found</h3>
            <p>Try adjusting your search or filter</p>
          </div>
        ) : (
          <table className="crl-table">
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Customer</th>
                <th>Furniture Type</th>
                <th>Description</th>
                <th>Images</th>
                <th>Quoted Price</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleRequests.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span className="crl-request-id">{r.id}</span>
                  </td>

                  <td>
                    <div className="crl-customer">
                      <div className="crl-avatar">
                        {r.adcustomer?.fullName // to get the initials of name
                          ?.split(" ")
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join("")}
                      </div>
                      <div>
                        <div className="crl-customer-name">
                          {r.adcustomer?.fullName}
                        </div>
                        <div className="crl-customer-email">
                          {r.adcustomer?.email}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className="crl-type-badge">{r.adproduct.name}</span>
                  </td>

                  <td>
                    <p className="crl-description">{r.description}</p>
                  </td>

                  <td>
                    <div className="crl-images-count">
                      <ImageIcon size={13} />
                      <span>{r.custom_cart_images?.length}</span>
                    </div>
                  </td>

                  <td>
                    {r.quotedPrice ? (
                      <span className="crl-quoted">
                        PKR {r.quotedPrice.toLocaleString()}
                      </span>
                    ) : (
                      <span className="crl-no-quote">—</span>
                    )}
                  </td>

                  <td className="crl-date">
                    {new Date(r.created_at).toLocaleDateString("en-PK", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>

                  <td>
                    <StatusBadge status={r.status} />
                  </td>

                  <td>
                    <button
                      className="crl-action-btn"
                      title="View Request"
                      onClick={() => navigate(`/admin/custom-orders/${r.id}`)}
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {visibleRequests.length > 0 && (
        <p className="crl-footer-count">
          Showing <strong>{visibleRequests.length}</strong> of{" "}
          <strong>{requests.length}</strong> requests
        </p>
      )}
    </div>
  );
};

export default CustomRequestList;
